import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import myAxios from "../AxiosUrl";
import uploadService from "../../services/uploadService";
import styles from "./TeamLogos.module.css";
import Loading from "../Loading/Loading";
import TeamLogoCard from "./TeamLogoCard";
import { CheckCircle, AlertCircle, Upload, Search, RefreshCw } from "lucide-react";

const TeamLogos = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, with-logo, without-logo
  const [stats, setStats] = useState({
    totalTeams: 0,
    teamsWithLogos: 0,
    teamsWithoutLogos: 0,
  });
  const [initializing, setInitializing] = useState(false);

  const itemsPerPage = 12;

  useEffect(() => {
    fetchTeamsWithStatus();
  }, []);

  const fetchTeamsWithStatus = async () => {
    try {
      setLoading(true);
      const response = await myAxios.get("/team-logo/teams-with-status");
      const data = response.data;

      if (data.success) {
        setTeams(data.data || []);
        setStats(data.stats || {
          totalTeams: 0,
          teamsWithLogos: 0,
          teamsWithoutLogos: 0,
        });
      }
    } catch (err) {
      setError("Error fetching teams with logo status");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeTeams = async () => {
    try {
      setInitializing(true);
      const response = await myAxios.post("/team-logo/initialize-teams");

      if (response.data.success) {
        const { stats } = response.data;
        setSuccessMessage(
          `Initialization complete! Initialized: ${stats.initialized}, Skipped: ${stats.skipped}, Errors: ${stats.errors}`
        );
        // Refresh the teams list
        await fetchTeamsWithStatus();

        setTimeout(() => setSuccessMessage(""), 5000);
      }
    } catch (err) {
      setError("Error initializing teams");
      console.error("Error:", err);
    } finally {
      setInitializing(false);
    }
  };

  const handleLogoUpload = async (teamId, file, onProgress = null) => {
    try {
      // Step 1: Upload to CDN with custom filename
      const uploadResult = await uploadService.uploadWithStandardName(file, "team", teamId, onProgress);

      if (!uploadResult.success) {
        const errorMsg = uploadResult.error || "Upload failed";
        setError(`Upload failed: ${errorMsg}`);
        setTimeout(() => setError(null), 5000);
        return;
      }

      // Step 2: Save CDN URL to API
      const cdnUrl = uploadResult.url;

      const response = await myAxios.post(
        `/team-logo/update/${teamId}`,
        { logoUrl: cdnUrl },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setSuccessMessage("Logo uploaded and saved successfully!");
        // Refresh the teams list
        await fetchTeamsWithStatus();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      // Extract detailed error message from backend response
      let errorMsg = "Error uploading logo";
      
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMsg = "Invalid logo URL or validation failed";
      } else if (err.response?.status === 500) {
        errorMsg = "Server error - please try again";
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      console.error("Upload error:", err);
      setTimeout(() => setError(null), 5000);
    }
  };

  const filteredTeams = teams.filter((team) => {
    const matchesSearch =
      team.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.code && team.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      team.teamId.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "with-logo" && team.hasLogo) ||
      (filterStatus === "without-logo" && !team.hasLogo);

    return matchesSearch && matchesFilter;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeams = filteredTeams.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.teamLogosContainer}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.teamLogosHeader}
      >
        <h1>Team Logo Management</h1>
        <p className={styles.subtitle}>Manage team logos across the platform</p>
      </motion.div>

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className={styles.statCard}
        >
          <div className={`${styles.statIcon} ${styles.total}`}>
            <span className="material-icons">groups</span>
          </div>
          <div className={styles.statContent}>
            <h3>{stats.totalTeams}</h3>
            <p>Total Teams</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className={styles.statCard}
        >
          <div className={`${styles.statIcon} ${styles.success}`}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <h3>{stats.teamsWithLogos}</h3>
            <p>With Logos</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className={styles.statCard}
        >
          <div className={`${styles.statIcon} ${styles.warning}`}>
            <AlertCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <h3>{stats.teamsWithoutLogos}</h3>
            <p>Need Logos</p>
          </div>
        </motion.div>
      </div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`${styles.alert} ${styles.alertSuccess}`}
          >
            <CheckCircle size={20} />
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`${styles.alert} ${styles.alertError}`}
          >
            <AlertCircle size={20} />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls */}
      <div className={styles.controlsSection}>
        <div className={styles.searchBox}>
          <Search size={20} />
          <input
            type="text"
            placeholder="Search teams by name, code, or ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className={styles.filterDropdown}>
          <select
            className={styles.filterSelect}
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Teams ({stats.totalTeams})</option>
            <option value="with-logo">With Logo ({stats.teamsWithLogos})</option>
            <option value="without-logo">Need Logo ({stats.teamsWithoutLogos})</option>
          </select>
        </div>

        <div className={styles.actionButtons}>
          <button
            className={styles.btnSecondary}
            onClick={fetchTeamsWithStatus}
            disabled={loading}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleInitializeTeams}
            disabled={initializing}
          >
            {initializing ? (
              <>
                <span className={styles.spinner}></span>
                Initializing...
              </>
            ) : (
              <>
                <Upload size={16} />
                Initialize Teams
              </>
            )}
          </button>
        </div>
      </div>

      {/* Teams Grid */}
      <div className={styles.teamsGrid}>
        <AnimatePresence>
          {paginatedTeams.map((team, index) => (
            <TeamLogoCard
              key={team.teamId}
              team={team}
              onUpload={handleLogoUpload}
              index={index}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={styles.paginationBtn}
          >
            Previous
          </button>
          <span className={styles.paginationInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={styles.paginationBtn}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamLogos;